import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import { LocalOffer as TagIcon } from '@mui/icons-material';

const MENU_PROPS = {
  PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 250 } },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

// Custom filters from Settings > Dynamic Filters (Value Packs) that are set to
// "Show in value pack form". Each one renders as its own card, like Target
// Industries: the filter label is the title, help text the description and its
// options the choices. Picked values are saved as filterValues[filter.key].
const CustomFilterFields = ({ filters, value, onChange, showErrors }) => {
  if (!filters.length) return null;

  const current = value && typeof value === 'object' ? value : {};

  const handleChange = (filter, picked) => {
    const values = (Array.isArray(picked) ? picked : [picked]).filter(Boolean);
    const next = { ...current };
    if (values.length) next[filter.key] = values;
    else delete next[filter.key];
    onChange(next);
  };

  return filters.map((filter) => {
    const multiple = filter.type === 'checkbox';
    const selected = current[filter.key] || [];
    const labelFor = (v) => filter.options.find((o) => o.value === v)?.label || v;
    const inputId = `filter-${filter.key}-label`;
    // "Required in the value pack form" (Settings > Dynamic Filters)
    const required = filter.source === 'custom' && Boolean(filter.requiredInForm);
    const missing = required && showErrors && !selected.length;

    return (
      <Grid item xs={12} md={6} key={filter.id}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {filter.label}
              {required && (
                <Typography component="span" color="error">
                  {' '}*
                </Typography>
              )}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {filter.helpText || `Select the ${filter.label.toLowerCase()} for this value pack`}
            </Typography>

            <FormControl fullWidth margin="normal" error={missing}>
              <InputLabel id={inputId}>{filter.label}</InputLabel>
              <Select
                labelId={inputId}
                multiple={multiple}
                value={multiple ? selected : selected[0] || ''}
                onChange={(e) => handleChange(filter, e.target.value)}
                input={<OutlinedInput label={filter.label} />}
                MenuProps={MENU_PROPS}
                renderValue={(picked) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(multiple ? picked : [picked]).map((v) => (
                      <Chip
                        key={v}
                        label={labelFor(v)}
                        size="small"
                        icon={<TagIcon />}
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Box>
                )}
              >
                {!multiple && (
                  <MenuItem value="" dense>
                    <em>None</em>
                  </MenuItem>
                )}
                {filter.options.map((option) => (
                  <MenuItem key={option.value} value={option.value} dense>
                    {multiple && <Checkbox size="small" checked={selected.includes(option.value)} />}
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {missing ? `Please select ${filter.label}` : multiple ? 'Pick one or more' : 'Pick one'}
                {filter.isActive === false ? ' · this filter is currently hidden on the storefront' : ''}
              </FormHelperText>
            </FormControl>
          </CardContent>
        </Card>
      </Grid>
    );
  });
};

CustomFilterFields.propTypes = {
  filters: PropTypes.arrayOf(PropTypes.object).isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  showErrors: PropTypes.bool,
};

CustomFilterFields.defaultProps = {
  value: {},
  showErrors: false,
};

export default CustomFilterFields;
